import { db } from '@app/icare/client/db'
import {
  localPaginatedQueryOptions,
  type ResultTypeFromQueryResult,
} from '@app/icare/client/db/client/localQueryOptions'
import { FormStatusBadge } from '@app/icare/components/status/FormStatusBadge'
import { queryKit } from '@app/icare/db/query/queryKit'
import { PatientFormTypeEnum } from '@app/icare/db/schema/form/PatientFormType'
import { ProgressReportStatusEnum, type ProgressReportStatusType } from '@app/icare/db/schema/form/ProgressReportForm'
import {
  type PatientIdType,
  type PatientStatus,
  PatientStatusEnum,
  type PatientStatusType,
} from '@app/icare/entities/patient/schema/Patient'
import { UserInfo } from '@app/icare/entities/user/components/UserInfo'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { createQueryStore, F, Q, QueryBaseSchema, useColumnDef } from '@pkg/api/query'
import { formatDate, formatDateTime } from '@pkg/shared/utils'
import { ButtonLink, FilterButton } from '@pkg/ui/button'
import { LinkIndicatorIcon, SearchInput } from '@pkg/ui/common'
import { FormattedPatientNameAndAddress } from '@pkg/ui/formattedContent'
import { usePageContainerOverride } from '@pkg/ui/layout/PageContainer'
import { QueryFilterWrapper } from '@pkg/ui/query/QueryFilterWrapper'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Descriptions, Space, Table } from 'antd'
import { sql } from 'kysely'
import { jsonObjectFrom } from 'kysely/helpers/sqlite'
import { useState } from 'react'
import { z } from 'zod'
import { useShallow } from 'zustand/react/shallow'

export const Route = createFileRoute('/_app/_dashboards/progress-reports')({
  component: RouteComponent,
})

const FilterSchema = QueryBaseSchema.extend({
  sortBy: z
    .enum([
      'patient.personal.lastName',
      'patientForm.createdBy',
      'patientForm.createdAt',
      'patientForm.form.contact.date',
      'patientForm.status',
    ])
    .default('patientForm.createdAt')
    .nullish(),
  filters: z
    .object({
      'patientForm.status': Q.in(z.enum(ProgressReportStatusEnum.$.zodEnum())).nullish(),
      createdBy: Q.in(z.string()).nullish(),
    })
    .optional(),
})

const PatientStatusValueSchema = z.enum(PatientStatusEnum.$.zodEnum())

const INITIAL_FILTERS = {
  'patientForm.status': F.in([ProgressReportStatusEnum.new]),
  'patient.status': F.in([PatientStatusEnum.new, PatientStatusEnum.open]),
}

const useProgressReportsQueryStore = createQueryStore({
  schema: FilterSchema,
  initialState: {
    filters: INITIAL_FILTERS,
    sortBy: 'patientForm.form.contact.date',
    sortDirection: 'ascend',
  },
})

function RouteComponent() {
  const queryStore = useProgressReportsQueryStore(useShallow(s => s))
  const [open, setOpen] = useState(false)

  const progressReportsParams = queryKit.parseQuery(queryStore, { paginationMode: 'offset' })
  const patientStatusFilter = progressReportsParams.filters.find(f => f.field === 'patient.status')
  const patientStatusValues: PatientStatusType[] =
    patientStatusFilter?.operator === 'in' && Array.isArray(patientStatusFilter.value)
      ? patientStatusFilter.value.flatMap(value => {
          const parsed = PatientStatusValueSchema.safeParse(value)
          return parsed.success ? [parsed.data] : []
        })
      : patientStatusFilter?.operator === 'eq'
        ? (() => {
            const parsed = PatientStatusValueSchema.safeParse(patientStatusFilter.value)
            return parsed.success ? [parsed.data] : []
          })()
        : []

  const { data, isFetching } = useQuery(
    localPaginatedQueryOptions({
      query: db
        .selectFrom('patientForm')
        .selectAll('patientForm')
        .select(sb =>
          jsonObjectFrom(
            sb
              .selectFrom('patient')
              .select(['id', 'personal', 'contact', 'status', 'address'])
              .whereRef('patient.id', '=', 'patientForm.patientId'),
          ).as('patient'),
        )
        .where('patientForm.type', '=', PatientFormTypeEnum.ProgressReport)
        .$if(patientStatusValues.length > 0, qb =>
          qb.where(({ exists, selectFrom }) =>
            exists(
              selectFrom('patient')
                .select('patient.id')
                .whereRef('patient.id', '=', 'patientForm.patientId')
                .where(eb => eb(eb.ref('patient.status', '->>').key('value'), 'in', patientStatusValues)),
            ),
          ),
        )
        .$call(queryKit.filters(progressReportsParams, ['patientForm.status']))
        .$call(queryKit.search(progressReportsParams))
        .$call(queryKit.deleted(progressReportsParams.deleted, db.dynamic.ref('patientForm.deletedAt')))
        .$if(progressReportsParams.sortBy === 'patientForm.form.contact.date', qb =>
          qb.orderBy(sql`json_extract("patientForm"."form", '$.contact.date')`, progressReportsParams.sortDir),
        )
        .$if(
          progressReportsParams.sortBy !== 'patientForm.form.contact.date' &&
            progressReportsParams.sortBy !== 'patient.personal.lastName',
          qb => qb.$call(queryKit.sort(progressReportsParams)),
        )
        .$if(progressReportsParams.sortBy === 'patient.personal.lastName', qb =>
          qb.orderBy(
            sql`(
              select json_extract("patient"."personal", '$.lastName')
              from "patient"
              where "patient"."id" = "patientForm"."patientId"
            )`,
            progressReportsParams.sortDir,
          ),
        )
        .$call(queryKit.paginate(progressReportsParams)),
      params: progressReportsParams,
    }),
  )

  type ProgressReportRecord = ResultTypeFromQueryResult<typeof data>

  const columns = useColumnDef<ProgressReportRecord>(
    () => [
      {
        key: 'patient.personal.lastName',
        title: <Trans>Patientenakte</Trans>,
        fixed: 'start',
        sorter: true,
        render: (_, record) =>
          record.patient && (
            <FormattedPatientNameAndAddress
              patient={{
                ...record.patient,
                id: record.patient.id as PatientIdType,
                status: record.patient.status as PatientStatus,
              }}
            />
          ),
      },
      {
        key: 'patientForm.status',
        title: t`Status`,
        sorter: true,
        render: (_, record) => (
          <FormStatusBadge<ProgressReportStatusType>
            value={record.status as ProgressReportStatusType}
            statusEnum={ProgressReportStatusEnum}
          />
        ),
      },
      {
        key: 'patientForm.form.contact.date',
        title: <Trans>Kontakt-/Besuchsdatum</Trans>,
        sorter: true,
        render: (_, record) => (record.form.contact?.date ? formatDate(record.form.contact?.date) : undefined),
      },
      {
        key: 'patientForm.id',
        title: t`Aktionen`,
        fixed: 'end',
        render: (_, record) => (
          <ButtonLink
            to="/patienten/$id/verlaufsdokumentation/$form"
            params={{ id: record.patientId, form: record.id }}
            size="small"
          >
            <Trans>zum Dokument</Trans>
            <LinkIndicatorIcon />
          </ButtonLink>
        ),
      },
    ],
    [],
    useProgressReportsQueryStore,
  )

  usePageContainerOverride(
    {
      header: {
        extra: (
          <Space size="small">
            <SearchInput
              size="small"
              value={queryStore.meta.searchRawValue}
              onChange={e => queryStore.setSearch(e.target.value)}
            />
            <FilterButton activeFilterCount={queryStore.meta.activeFilterCount} onClick={() => setOpen(true)} />
          </Space>
        ),
      },
    },
    [queryStore.meta.searchRawValue, queryStore.setSearch, queryStore.meta.activeFilterCount, setOpen, queryStore],
  )
  return (
    <QueryFilterWrapper
      queryStore={useProgressReportsQueryStore}
      open={open}
      onOpenChange={setOpen}
      filterConfig={[
        {
          name: 'patient.status',
          label: <Trans>Status der Patientenakte</Trans>,
          mode: 'multiple',
          options: PatientStatusEnum.$.options,
        },
        {
          name: 'patientForm.status',
          label: <Trans>Status der Verlaufsdokumentation</Trans>,
          mode: 'multiple',
          options: ProgressReportStatusEnum.$.options,
        },
      ]}
    >
      <Table<ProgressReportRecord>
        dataSource={data?.data ?? []}
        loading={isFetching}
        rowKey="id"
        size="small"
        columns={columns}
        pagination={data}
        onChange={queryStore.onChange}
        scroll={{ x: 'max-content' }}
        expandable={{
          expandedRowRender: record => (
            <Descriptions
              column={1}
              size="small"
              style={{ paddingLeft: 32 }}
              items={[
                {
                  label: t`Erstellt von`,
                  key: 'createdBy',
                  children: <UserInfo userId={record.createdBy} />,
                },
                {
                  label: t`Erstellt am`,
                  key: 'createdAt',
                  children: formatDateTime(record.createdAt),
                },
              ]}
            />
          ),
        }}
      />
    </QueryFilterWrapper>
  )
}
